<script setup>
import { Head } from '@inertiajs/vue3';
import { computed, watch } from 'vue';

const props = defineProps({
    title: { type: String, required: true },
    seo: { type: Object, default: () => ({}) },
});

const seoTitle = computed(() => props.seo.title ?? props.title);
const description = computed(() => props.seo.description ?? 'Find and organize local sports games with SportsDock.');
const canonicalUrl = computed(() => props.seo.canonicalUrl ?? '/');
const alternateUrl = computed(() => props.seo.alternateUrl ?? null);
const currentLocale = computed(() => props.seo.htmlLang ?? 'en-CA');
// Inertia changes the page without replacing the document's html element.
watch(currentLocale, (locale) => {
    if (typeof document !== 'undefined') {
        document.documentElement.lang = locale;
    }
}, { immediate: true });
const alternateLocale = computed(() => currentLocale.value === 'fr-CA' ? 'en-CA' : 'fr-CA');
const ogLocale = computed(() => props.seo.locale ?? currentLocale.value.replace('-', '_'));
const ogAlternateLocale = computed(() => props.seo.alternateLocale ?? alternateLocale.value.replace('-', '_'));
const imageUrl = computed(() => props.seo.imageUrl ?? '/images/landing/hero-players-court.png');
const robots = computed(() => props.seo.robots ?? 'index,follow,max-image-preview:large');
const xDefaultUrl = computed(() => {
    if (currentLocale.value.startsWith('fr')) {
        return canonicalUrl.value;
    }

    return alternateUrl.value ?? canonicalUrl.value;
});
</script>

<template>
    <Head :title="title">
        <meta head-key="description" name="description" :content="description">
        <meta head-key="robots" name="robots" :content="robots">
        <link head-key="canonical" rel="canonical" :href="canonicalUrl">
        <template v-if="alternateUrl">
            <link head-key="alternate-current" rel="alternate" :hrefLang="currentLocale" :href="canonicalUrl">
            <link head-key="alternate-other" rel="alternate" :hrefLang="alternateLocale" :href="alternateUrl">
            <link head-key="alternate-default" rel="alternate" hrefLang="x-default" :href="xDefaultUrl">
        </template>
        <link head-key="sitemap" rel="sitemap" type="application/xml" href="/sitemap.xml">
        <meta head-key="og-type" property="og:type" content="website">
        <meta head-key="og-site-name" property="og:site_name" content="SportsDock">
        <meta head-key="og-title" property="og:title" :content="seoTitle">
        <meta head-key="og-description" property="og:description" :content="description">
        <meta head-key="og-url" property="og:url" :content="canonicalUrl">
        <meta head-key="og-locale" property="og:locale" :content="ogLocale">
        <meta head-key="og-alternate-locale" property="og:locale:alternate" :content="ogAlternateLocale">
        <meta head-key="og-image" property="og:image" :content="imageUrl">
        <meta head-key="og-image-width" property="og:image:width" content="1420">
        <meta head-key="og-image-height" property="og:image:height" content="883">
        <meta head-key="twitter-card" name="twitter:card" content="summary_large_image">
        <meta head-key="twitter-title" name="twitter:title" :content="seoTitle">
        <meta head-key="twitter-description" name="twitter:description" :content="description">
        <meta head-key="twitter-image" name="twitter:image" :content="imageUrl">
    </Head>
</template>
